import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Users, FolderKanban, CheckSquare, Building2, LogOut, UserCircle, Bell, DollarSign, HardDrive, Settings, ShieldCheck, FileText, UserPlus, FileCode2 } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useSettings } from '../context/SettingsContext';
import { useLanguage } from '../context/LanguageContext';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/login'); };
  const { settings } = useSettings();
  let permissions = {};
  try { permissions = JSON.parse(settings.permissions || '{}'); } catch (_) {}
  const can = (key, fallback = true) => permissions?.[user?.role]?.[key] ?? fallback;

  const adminNav = [
    { to: '/dashboard', icon: <LayoutDashboard />, label: t('dashboard') },
    { to: '/leads', icon: <UserPlus />, label: 'Leads' },
    { to: '/embedded-forms', icon: <FileCode2 />, label: 'Forms' },
    can('clients') && { to: '/clients', icon: <Building2 />, label: t('clients') },
    can('services') && { to: '/services', icon: <FolderKanban />, label: t('services') },
    { to: '/tasks', icon: <CheckSquare />, label: t('tasks') },
    { to: '/team', icon: <Users />, label: t('team') },
    can('quotations') && { to: '/quotations', icon: <FileText />, label: 'Quotations' },
    can('invoices') && { to: '/finance', icon: <DollarSign />, label: t('finance') },
    can('files') && { to: '/drive', icon: <HardDrive />, label: t('drive') },
    can('settings', user?.role === 'admin') && { to: '/settings', icon: <Settings />, label: t('settings') },
    { to: '/audit-log', icon: <ShieldCheck />, label: t('auditLog') },
  ].filter(Boolean);
  const clientNav = [
    { to: '/contract', icon: <FolderKanban />, label: t('services') },
    { to: '/quotations', icon: <FileText />, label: 'Quotations' },
    { to: '/finance', icon: <DollarSign />, label: t('invoices') },
    { to: '/drive', icon: <HardDrive />, label: t('myDrive') },
  ];
  const employeeNav = [
    { to: '/dashboard', icon: <LayoutDashboard />, label: t('dashboard') },
    { to: '/tasks', icon: <CheckSquare />, label: t('myTasks') },
    can('invoices', false) && { to: '/finance', icon: <DollarSign />, label: t('finance') },
    can('quotations', false) && { to: '/quotations', icon: <FileText />, label: 'Quotations' },
    can('files', false) && { to: '/drive', icon: <HardDrive />, label: t('drive') },
    can('clients', false) && { to: '/clients', icon: <Building2 />, label: t('clients') },
  ].filter(Boolean);

  const navItems = user?.role === 'client' ? clientNav : user?.role === 'employee' ? employeeNav : adminNav;
  const { unreadCount } = useNotifications();
  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';

  const logoUrl = settings.logoUrl || null;
  const systemName = settings.systemName || 'The Black Div';
  const systemSubtitle = settings.systemSubtitle || 'Client Portal';
  const firstLetter = systemName[0]?.toUpperCase() || 'B';

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <div className="sidebar-logo-icon" style={{ overflow: 'hidden', background: logoUrl ? 'transparent' : undefined }}>
          {logoUrl
            ? <img src={logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span>{firstLetter}</span>
          }
        </div>
        <div>
          <div className="sidebar-logo-text">{systemName}</div>
          <div className="sidebar-logo-sub">{systemSubtitle}</div>
        </div>
      </div>
      <nav className="sidebar-nav">
        <div className="nav-section-title">Menu</div>
        {navItems.map(item => (
          <NavLink key={item.to} to={item.to} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
            {item.icon}<span>{item.label}</span>
          </NavLink>
        ))}
        <div className="nav-section-title" style={{ marginTop: 24 }}>Account</div>
        <NavLink to="/notifications" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
          <div style={{ position: 'relative' }}>
            <Bell size={18} />
            {unreadCount > 0 && (
              <span style={{ position: 'absolute', top: -6, right: -8, background: 'var(--red)', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 10, minWidth: 15, height: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
          <span>{t('notifications')}</span>
        </NavLink>
        <NavLink to="/profile" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
          <UserCircle /><span>{t('profile')}</span>
        </NavLink>
      </nav>
      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-avatar">{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
            <div className="user-role">{user?.role}</div>
          </div>
          <button onClick={handleLogout} title="Logout" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', padding: '4px' }}>
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
